<script>
  import { onMount } from "svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";
  import QrCode from "svelte-qrcode";
  import { useUrlParams } from "@stores/urlStore.js";
  import { projectListStore } from "@stores/damProjectsStore.js";
  import { useNavigate } from "svelte-navigator";
  import { fly } from "svelte/transition";
  import Header from "@components/Header.svelte";
  import ContentSwitcher from "lib/components/ContentSwitcher.svelte";
  import DataTableBlack from "@components/DataTableBlack.svelte";
  import { PROJECT_STAGES } from "@utils/stagesAndSteps.js";

  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import LimitationList from "../components/LimitationList.svelte";
  import ParticipantLimitationList from "../components/ParticipantLimitationList.svelte";
  import Modal from "../../../../lib/components/Modal.svelte";
  import ProjectStepActions from "../components/ProjectStepActions.svelte";
  import {
    BadgeColor,
    Button,
    InfoBlock,
    SaveStatus,
    SectionWrapper,
  } from "paxray-components-svelte";
  import { ROUTES } from "@utils/routes.js";
  import {
    appDisplayName,
    presetUrlToDomain,
  } from "lib/util/restrictionRules.js";

  export let isCompleted = false;

  // Presets that are often used for work. All other presets are recommended.
  // Matched by name because preset ids differ between environments.
  // Keep in sync with the "Often used for work" descriptions in the backend.
  const REVIEW_PRESET_NAMES = new Set([
    "Banking",
    "Broker",
    "Games",
    "Health",
    "Jobsearch",
    "Mail",
    "News",
    "Pharmacy",
    "Politics and Unions",
    "Private Messengers",
    "Shopping",
    "Social Media",
    "Taxes",
    "Video",
  ]);

  const navigate = useNavigate();
  const urlParams = useUrlParams();
  const step = PROJECT_STAGES.SETUP.steps.BLKLST;
  const stage = PROJECT_STAGES.SETUP.id;
  const { addLimitationPreset, removeLimitationPreset, getProjectLimitations } =
    projectListStore;

  let baseurl = "";
  let stepCompleted = false;
  let limitationPresets = [];
  let selectedPresetIds = [];
  let presetPage = 1;
  let presetPageSize = 10;
  let presetFilter = 0;
  const presetFilterOptions = ["All", "Recommended", "Review"];
  let expandedPresetIds = [];
  // Items per preset id, loaded on first expand: { loading, error, items }.
  let presetItems = {};
  let qrModalOpen = false;
  let stageId = null;
  let selectionReady = false;
  let syncing = false;
  let pendingSync = false;
  let exclusionSaving = false;
  let participantSaving = false;
  let participantExclusionCount = null;
  let participantEntries = [];
  const countLabel = (count, singular, plural) =>
    `${count} ${count === 1 ? singular : plural}`;
  // Summary in the "Complete & Lock" dialog
  $: completionDetails = [
    countLabel(selectedPresetIds.length, "exclusion preset", "exclusion presets"),
    countLabel(participantExclusionCount ?? 0, "participant exclusion", "participant exclusions"),
    countLabel(projectExclusionCount ?? 0, "additional exclusion", "additional exclusions"),
  ];
  let projectExclusionList;
  let projectExclusionCount = null;
  // Without single exclusions the table is hidden, the hint offers to add the first one
  $: noProjectExclusions = projectExclusionCount === 0;
  let loadError = false;
  let syncError = false;
  let showBlacklistGuide = false;
  let blacklistGuideStep = 0;
  let blacklistGuideSteps = [];

  onMount(() =>
    registerViewTour(() => {
      blacklistGuideSteps = [
        {
          target: '[data-guide="exclusion-overview"]',
          title: "Exclude apps and websites before Scoping",
          text: "An optional first filter that keeps private apps and websites out of data collection from the start. The final recording scope is defined later with the whitelist. Completing this step is the last Setup task before Scoping.",
        },
        {
          target: '[data-guide="exclusion-presets"] table',
          title: "Review excluded categories",
          text: editable
            ? "Apps and websites in the selected categories are excluded from data collection. This protects private areas like banking or dating. Recommended categories are already selected. To include a category in data collection, clear its checkbox. Changes are saved automatically."
            : "Apps and websites in the selected categories are excluded from data collection. This protects private areas like banking or dating. The Included column shows which categories apply to this project.",
        },
        {
          target: '[data-guide="exclusion-presets"] .preset-filter',
          title: "Filter categories",
          text: editable
            ? "Categories marked Review are often needed for work. Use Review to see only these and select them only if they are not used in this project."
            : "Use Recommended or Review to show only these categories.",
        },
        {
          target:
            '[data-guide="exclusion-presets"] tbody .bx--table-expand__button',
          title: "See what a category contains",
          text: "Expand a row to see the apps and websites a category excludes.",
        },
        {
          target: '[data-guide="participant-exclusions"]',
          title: "Review participant exclusions",
          text: editable
            ? "Participants can exclude their own apps and websites on the participant page. Check their entries here. Only edit or delete an entry after agreeing with the employees concerned."
            : "These apps and websites were excluded by participants on the participant page.",
        },
        {
          target: '[data-guide="additional-exclusions"]',
          title: "Add further exclusions if needed",
          text: editable
            ? "Additional exclusions are optional. Use Add App or Website if a single app or website is not yet covered by the categories or participant exclusions. Saved entries can be edited or deleted in the row actions."
            : "These single apps and websites are excluded in addition to the categories and participant exclusions.",
        },
        {
          target:
            '[data-guide="complete-exclusions"] > button, [data-guide="complete-exclusions"] > .completed-badge',
          title: "Complete and lock exclusions",
          text: 'Check the selected categories, participant exclusions and additional exclusions, then use "Complete & Lock Exclusions". After that, participants can no longer add exclusions. Completing locks the settings and cannot be undone. Scoping becomes available once all required Setup tasks are complete.',
        },
      ].filter(({ target }) => {
        const element = document.querySelector(target);
        return (
          element &&
          getComputedStyle(element).visibility === "visible" &&
          element.getBoundingClientRect().width > 0 &&
          element.getBoundingClientRect().height > 0
        );
      });
      blacklistGuideStep = 0;
      showBlacklistGuide = blacklistGuideSteps.length > 0;
    }),
  );

  $: ({ idproject } = $urlParams);
  $: projectProgressHref = `${ROUTES.dam.progress}?${new URLSearchParams({ idproject }).toString()}`;
  $: editable = selectionReady && !isCompleted && !stepCompleted && !syncError;
  $: ({ idLimitation } = $projectListStore);

  $: blacklistPresets = limitationPresets
    .filter((row) => row.type === "Blacklist")
    .map((row) => ({
      ...row,
      recommended: !REVIEW_PRESET_NAMES.has(row.name),
    }));
  $: filteredPresets = blacklistPresets.filter((row) => {
    if (presetFilter === 1 && !row.recommended) return false;
    return !(presetFilter === 2 && row.recommended);
  });
  // The header checkbox replaces the whole selection with the visible rows,
  // so it is hidden while a filter hides some presets.
  $: presetsFiltered = filteredPresets.length !== blacklistPresets.length;
  $: presetFilter, (presetPage = 1);

  $: expandedPresetIds.forEach(loadPresetItems);

  // Readable labels for preset items, deduplicated. Website rules are stored as
  // regex, so wildcards are shown as "*" (e.g. ksk-*.de).
  function toPresetItemLabels(items) {
    const labels = items.map((item) =>
      item.url
        ? presetUrlToDomain(item.url)
        : item.name || appDisplayName(item.appname),
    );
    return [...new Set(labels)].sort((a, b) => a.localeCompare(b));
  }

  // Limits expanded content to the Category and Description columns. The
  // expanded cell spans all columns, so the edges are taken from the headers.
  function alignToPresetColumns(node) {
    const table = node.closest("table");
    const update = () => {
      const category = table?.querySelector(
        'thead th[data-header="name"] .bx--table-header-label',
      );
      const description = table?.querySelector(
        'thead th[data-header="description"]',
      );
      if (!category || !description) return;
      const container = node.parentElement.getBoundingClientRect();
      node.style.marginLeft = `${category.getBoundingClientRect().left - container.left}px`;
      node.style.marginRight = `${container.right - description.getBoundingClientRect().right}px`;
    };
    update();
    const observer = new ResizeObserver(update);
    if (table) observer.observe(table);
    return { destroy: () => observer.disconnect() };
  }

  async function loadPresetItems(presetId) {
    // Failed loads are retried on the next expand.
    if (presetItems[presetId] && !presetItems[presetId].error) return;
    presetItems = {
      ...presetItems,
      [presetId]: { loading: true, error: false, items: [] },
    };
    try {
      const response = await fetchWithSession(
        `/api/damprojects/limitation-presets/${presetId}`,
      );
      if (!response.ok) throw new Error("Could not load preset items");
      const data = await response.json();
      presetItems = {
        ...presetItems,
        [presetId]: {
          loading: false,
          error: false,
          items: toPresetItemLabels(data.items || []),
        },
      };
    } catch (err) {
      console.error("Error loading preset items:", err);
      presetItems = {
        ...presetItems,
        [presetId]: { loading: false, error: true, items: [] },
      };
    }
  }

  $: {
    baseurl =
      window.location.protocol +
      "//" +
      window.location.host +
      (window.location.port ? ":" + window.location.port : "");
  }

  $: if (editable) syncSelectedPresets(selectedPresetIds);

  async function syncSelectedPresets(selectedIds) {
    if (!stageId || !editable || syncError) return;
    if (syncing) {
      pendingSync = true;
      return;
    }
    syncing = true;
    try {
      const stagePresets =
        $projectListStore.projectStageLimitationPresets || [];
      const existingIds = new Set(
        stagePresets.map((p) => String(p.limitation_preset_id)),
      );
      const selectedIdsSet = new Set(selectedIds.map(String));
      const blacklistIds = new Set(
        blacklistPresets.map((preset) => String(preset.id)),
      );

      const toAdd = selectedIds.filter((id) => !existingIds.has(String(id)));
      const toRemove = stagePresets.filter(
        (p) =>
          blacklistIds.has(String(p.limitation_preset_id)) &&
          !selectedIdsSet.has(String(p.limitation_preset_id)),
      );

      if (toAdd.length === 0 && toRemove.length === 0) return;

      const results = await Promise.allSettled([
        ...toAdd.map((presetId) => addLimitationPreset(presetId, stageId)),
        ...toRemove.map((p) => removeLimitationPreset(p.id)),
      ]);
      if (
        results.some(
          (result) => result.status === "rejected" || !result.value?.ok,
        )
      ) {
        throw new Error("Could not save all exclusion preset changes");
      }
    } catch (err) {
      syncError = true;
      console.error("Error syncing limitation presets:", err);
    } finally {
      syncing = false;
      if (pendingSync) {
        pendingSync = false;
        syncSelectedPresets(selectedPresetIds);
      }
    }
  }

  onMount(async () => {
    try {
      if (!idproject) {
        navigate(ROUTES.dam.root);
        return;
      }
      const progress = await projectListStore.getProjectProgress(idproject, {
        force: true,
      });
      if (!progress) throw new Error("Could not load project progress");
      stepCompleted = await projectListStore.isStepCompleted(
        idproject,
        step,
        stage,
      );
      await getProjectLimitations(idproject, stage);
      stageId = projectListStore.getProjectStageId(stage);

      if (!stageId) {
        throw new Error(`No stageId found for stage: ${stage}`);
      }
      const savedPresets = await projectListStore.getLimitationPresets(stageId);
      if (!savedPresets)
        throw new Error("Could not load saved exclusion presets");
      const response = await fetchWithSession(
        `/api/damprojects/limitation-presets`,
      );
      if (!response.ok) throw new Error("Could not load exclusion presets");
      limitationPresets = await response.json();
      const savedPresetIds = new Set(
        savedPresets.map((preset) => String(preset.limitation_preset_id)),
      );
      const blacklist = limitationPresets.filter(
        (preset) => preset.type === "Blacklist",
      );
      selectedPresetIds = blacklist
        .filter((preset) => savedPresetIds.has(String(preset.id)))
        .map((preset) => preset.id);
      // Opt-out: preselect the recommended presets while nothing is saved yet.
      // An intentionally empty selection cannot be told apart and is
      // preselected again on the next visit.
      if (selectedPresetIds.length === 0 && !stepCompleted && !isCompleted) {
        selectedPresetIds = blacklist
          .filter((preset) => !REVIEW_PRESET_NAMES.has(preset.name))
          .map((preset) => preset.id);
      }

      selectionReady = true;
    } catch (err) {
      loadError = true;
      console.error("Error loading blacklisting data:", err);
    }
  });

  $$restProps;
</script>

<Header
  section="Project"
  area="Setup"
  breadcrumb="Exclude Apps & Websites"
  areaHref={`${projectProgressHref}#setup-exclusions`}
  tooltipSections={[
    {
      text: "An optional first filter before the whitelist.\nIt keeps private apps and websites out of data collection from the start.\nThe final recording scope is defined later with the whitelist.",
    },
    {
      heading: "Exclusion presets",
      text: "Apps and websites in the selected categories are excluded.\nRecommended categories are already selected.\nSelect categories marked Review only if they are not used in this project.",
    },
    {
      heading: "Participant exclusions",
      text: "Apps and websites your participants excluded themselves.",
    },
    {
      heading: "Additional exclusions",
      text: "Single apps or websites you add if something is still missing.",
    },
  ]}
>
  <!-- Nur die Kategorie-Checkboxen speichern automatisch, ohne Kategorien gibt es keinen Speicherstand -->
  {#if editable && blacklistPresets.length}
    <SaveStatus saving={syncing || pendingSync || exclusionSaving || participantSaving} />
  {/if}
  <div data-guide="complete-exclusions">
    <ProjectStepActions
      {idproject}
      {step}
      {stage}
      showGoBackButton={false}
      disabled={!editable || syncing || pendingSync}
      allowAdminOverride={false}
      text="Complete & Lock Exclusions"
      size="sm"
      completionWarning="After completion, the exclusion settings cannot be changed and participants can no longer add exclusions. Please make sure the rules below are correct before continuing. This action cannot be undone."
      {completionDetails}
    />
  </div>
</Header>

<SectionWrapper fullscreen={true} seamless={true} seamlessPadding={true}>
  <div class="main" slot="main">
    {#if loadError || syncError}
      <InfoBlock
        type="warning"
        heading={loadError
          ? "Exclusions could not be loaded"
          : "Selection could not be saved"}
        message="Reload this page to restore the saved settings before making further changes or completing this step."
        showClose={false}
      />
    {/if}
    <div data-guide="exclusion-overview">
      <InfoBlock
        type="info"
        heading="Exclusion presets"
        message={isCompleted || stepCompleted
          ? "The project settings are completed and locked. Review the selected presets, participant exclusions and additional exclusions below."
          : "Selected categories are excluded from data collection. Check the Review tab and adjust if needed."}
        showClose={false}
      />
    </div>
    <div
      class="blacklisting-presets-table"
      class:presets-filtered={presetsFiltered}
      data-guide="exclusion-presets"
    >
      <DataTableBlack
        headers={[
          { key: "name", value: "Category" },
          { key: "description", value: "Description" },
          { key: "recommended", value: "Recommendation" },
          ...(!editable ? [{ key: "included", value: "Included" }] : []),
        ]}
        rows={filteredPresets}
        sortable
        slicePage
        batchSelection={editable}
        expandable
        bind:expandedRowIds={expandedPresetIds}
        bind:selectedRowIds={selectedPresetIds}
        bind:page={presetPage}
        bind:pageSize={presetPageSize}
        totalItems={filteredPresets.length}
        pageSizes={[10, 25, 50]}
        showSearch={false}
        showNewButton={false}
      >
        <div slot="left-toolbar" class="preset-filter">
          <ContentSwitcher
            options={presetFilterOptions}
            bind:selected={presetFilter}
          />
        </div>
        <svelte:fragment slot="cell" let:row let:cell>
          {#if cell.key === "recommended"}
            <BadgeColor
              color={cell.value ? "green" : "yellow"}
              text={cell.value ? "Recommended" : "Review"}
              small
            />
          {:else if cell.key === "included"}
            {selectedPresetIds.includes(row.id) ? "Yes" : "No"}
          {:else}
            {cell.value}
          {/if}
        </svelte:fragment>
        <svelte:fragment slot="expanded-row" let:row>
          {@const state = presetItems[row.id]}
          <div class="preset-items-wrapper" use:alignToPresetColumns>
          {#if !state || state.loading}
            <p class="preset-items-hint">Loading apps and websites</p>
          {:else if state.error}
            <p class="preset-items-hint">
              Apps and websites could not be loaded. Collapse and expand the row
              to try again.
            </p>
          {:else if state.items.length === 0}
            <p class="preset-items-hint">This preset has no apps or websites.</p>
          {:else}
            <ul class="preset-items">
              {#each state.items as item (item)}
                <li>
                  <BadgeColor text={item} color="light-grey" small border />
                </li>
              {/each}
            </ul>
          {/if}
          </div>
        </svelte:fragment>
      </DataTableBlack>
    </div>

    <ParticipantLimitationList
      {idproject}
      {editable}
      bind:saving={participantSaving}
      bind:entryCount={participantExclusionCount}
      bind:entries={participantEntries}
    />

    <div data-guide="additional-exclusions">
    <InfoBlock
      type="info"
      heading="Additional exclusions (optional)"
      message={noProjectExclusions
        ? editable
          ? "The categories and participant exclusions above usually cover private areas. If needed, you can exclude further single apps or websites."
          : "No additional apps or websites are excluded in this project."
        : isCompleted || stepCompleted
          ? "Single apps and websites excluded in addition to the exclusions above."
          : "Single apps and websites you exclude in addition to the exclusions above."}
      showClose={false}
      showActions={noProjectExclusions && editable}
    >
      <svelte:fragment slot="actions">
        <Button
          kind="secondary"
          size="field"
          text="Add App or Website"
          on:click={() => projectExclusionList?.openAddDialog()}
        />
      </svelte:fragment>
    </InfoBlock>
    </div>

    <!-- 
    <div>
      If you want to get iser input for the limitations, you can access the
      workshop view for this project at: <br /><br />
      <table>
        <tr>
          <td style="vertical-align:top">
            <CodeSnippet code={baseurl + "/UserInput?idproject=" + idproject}
            ></CodeSnippet>
          </td><td>
            <Button
              size="field"
              kind="tertiary"
              on:click={() => {
                qrModalOpen = true;
              }}>Show QR Code</Button
            >
          </td>
        </tr>
      </table>
    </div> -->

    <!-- Without a table the wrapper is empty, display contents keeps it out of the gap. -->
    <div
      data-guide="project-exclusions"
      style:display={noProjectExclusions ? "contents" : null}
    >
      <LimitationList
        bind:this={projectExclusionList}
        {editable}
        showTypeSelector={false}
        showAddIcon={false}
        showTable={!noProjectExclusions}
        bind:entryCount={projectExclusionCount}
        showSuccessToast={false}
        activePresetIds={selectedPresetIds}
        participantEntries={participantEntries}
        bind:saving={exclusionSaving}
      />
    </div>

  </div>
</SectionWrapper>

<SpotlightGuide
  bind:open={showBlacklistGuide}
  bind:currentStep={blacklistGuideStep}
  steps={blacklistGuideSteps}
  allowInteraction={false}
/>

<Modal
  autowidth
  bind:open={qrModalOpen}
  preventCloseOnClickOutside
  actions
  modalHeading="Workshop QR Code"
  primaryButtonText="Ok"
  secondaryButtonText="Cancel"
  secondaryButtonAction={() => {
    qrModalOpen = false;
  }}
  primaryButtonAction={() => {
    qrModalOpen = false;
  }}
  on:close={() => {
    qrModalOpen = false;
  }}
>
  <div style="margin:0 auto; width:300px;">
    <QrCode value={baseurl + "/UserInput?idproject=" + idproject} />
    <h4>{baseurl + "/UserInput?idproject=" + idproject}</h4>
  </div>
</Modal>

<style>
  .main {
    max-width: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  /* Expanded preset rows: drop Carbon's separator lines between a row and its
     item chips, so both read as one row. */
  :global(
      .blacklisting-presets-table
        .bx--data-table
        tr.bx--expandable-row.bx--parent-row
        td
    ) {
    border-bottom-color: transparent !important;
  }

  :global(
      .blacklisting-presets-table
        .bx--data-table
        tr.bx--expandable-row:not(.bx--parent-row)
        td
    ) {
    border-top: 0 !important;
    box-shadow: none !important;
  }

  /* Carbon gives the column after the expand and checkbox columns only 0.5rem
     padding, while its header label also has the 1rem sort button padding.
     Matching both keeps the category header aligned with its cells. */
  :global(
      .blacklisting-presets-table
        .bx--data-table
        td.bx--table-expand
        + .bx--table-column-checkbox
        + td
    ) {
    padding-left: 1.5rem;
  }

  /* Keeps the filter left-aligned, as the toolbar has no search here. */
  .preset-filter {
    margin-right: auto;
  }

  /* Without the search field, the toolbar background shows as a grey bar. */
  :global(.blacklisting-presets-table .bx--table-toolbar) {
    background-color: transparent;
  }

  .preset-items-wrapper {
    padding-bottom: var(--control-gap);
  }

  .preset-items-hint {
    color: var(--pax-grey);
  }

  /* Keeps the gaps around both table toolbars at --control-gap instead of
     Carbon's toolbar height and container padding. */
  .main :global(.bx--data-table-container) {
    padding-top: 0;
  }

  .main :global(.bx--table-toolbar) {
    min-height: 0;
    margin-bottom: var(--control-gap);
  }

  .main :global(.bx--table-toolbar .bx--toolbar-content) {
    margin-bottom: 0;
  }

  .preset-items {
    display: flex;
    flex-wrap: wrap;
    gap: var(--control-gap);
    max-height: 16rem;
    overflow-y: auto;
  }

  :global(
      .blacklisting-presets-table.presets-filtered
        .bx--data-table
        thead
        .bx--table-column-checkbox
        > *
    ) {
    visibility: hidden;
  }

  :global(
      .blacklisting-presets-table .custom-table .bx--data-table th:last-child
    ),
  :global(
      .blacklisting-presets-table .custom-table .bx--data-table td:last-child
    ),
  :global(
      .blacklisting-presets-table
        .custom-table
        .bx--data-table
        th:last-child
        .bx--table-sort
    ),
  :global(
      .blacklisting-presets-table
        .custom-table
        .bx--data-table
        th:last-child
        .bx--table-header-label
    ) {
    text-align: left !important;
    justify-content: flex-start !important;
  }
</style>
